import { useState, useEffect, useCallback } from "react";
import { FaCheck, FaEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportOptions from "../ExportOptions"; // Ensure this path is correct
import PageHeading from "../PageHeading"; // Ensure this path is correct
import TableSkeleton from "../TableSkeleton"; // Ensure this path is correct
import ConfirmActionModal from "../ConfirmDeleteModal"; // Ensure this path is correct
import ViewReturnModal from "./ViewReturnModal"; // Ensure this path is correct
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../Shared/Pagination";

const WastageReturnList = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewReturn, setViewReturn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIds, setDeleteIds] = useState(null);
  const [type] = useState("Wastage");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const limit = 10;
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_BASE_URL = "https://api.muktihospital.com";

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Return"},
    { label: "Wastage Returns" },
  ];

  const skeletonColumns = [
    { width: 40, height: 16, isCircle: false, className: "text-left", skeletonClassName: "ml-3" },
    { width: 50, height: 16, isCircle: false, className: "text-left" },
    { width: 120, height: 16, isCircle: false, className: "text-left" },
    { width: 150, height: 16, isCircle: false, className: "text-left" },
    { width: 100, height: 16, isCircle: false, className: "text-left" },
    { width: 100, height: 16, isCircle: false, className: "text-end" },
    { width: 120, height: 16, isCircle: false, className: "text-end" },
    { width: 120, height: 16, isCircle: false, className: "text-end" },
  ];

  const headers = ["Sl", "Bill Number", "Medicine Name", "Date", "Source Type", "Total Amount(৳)"]; 

  // Data mapper for export functionality
const dataMapper = useCallback(() => {
  let counter = 0;
    return (returnData) => {
      counter += 1;
    return [
      counter,
      returnData?.billNumber || "N/A",
      returnData?.medicineName || "Unknown",
      new Date(returnData?.wastedAt || new Date()).toLocaleDateString(),
      returnData?.sourceType || "Unknown",
      returnData?.totalAmount || 0,
    ].join("\t");
  }
  })();

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const fetchReturns = async () => {
  setLoading(true);
  setError("");
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/return/wastage-returns?page=${currentPage}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
      }
    );

    const data = await response.json();
    // console.log("Raw API response for wastage returns:", data);
    if (response.ok && data.returns) {
      const processedReturns = data.returns.map((ret) => ({
        ...ret,
        type: "Wastage",
        adjustmentType: "wastage",
        returnedAt: ret.wastedAt,
        items: [
          {
            id: ret.pharmacyBillItemId || ret.purchaseMedicineItemId || ret.id,
            medicineName: ret.medicineName || "N/A",
            quantity: ret.quantity || 0,
            salePrice: ret.salePrice || 0,
            tax: ret.tax || 0,
            total: ret.totalAmount || (ret.quantity * ret.salePrice * (1 + (ret.tax || 0) / 100)).toFixed(2),
            expiryDate: ret.expiryDate,
          },
        ],
        // Optionally add source details if needed
        ...(ret.purchaseMedicineItemId && {
          purchase: { id: ret.billId, billNo: ret.billNumber },
        }),
      }));
      // console.log("Processed returns:", processedReturns);
      setReturns(processedReturns);
      setFilteredReturns(processedReturns);
      setTotalPages(data.pages || 1);
      setSelectedIds([]);
    } else {
      setError(data.error || `Failed to fetch wastage returns (Status: ${response.status})`);
    }
  } catch (err) {
    setError("Unable to connect to the server. Please try again later.");
    console.error("Fetch wastage returns error:", err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchReturns();
  }, [currentPage]);

  useEffect(() => {
    let filtered = returns;

    if (searchQuery) {
      filtered = filtered.filter(
        (returnData) =>
          (returnData.billNumber?.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (returnData.medicineName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (returnData.batchNo?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (startDate && endDate) {
      filtered = filtered.filter((returnData) => {
        const returnDate = new Date(returnData.wastedAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Ensure comparison is only on date part
        start.setHours(0,0,0,0);
        end.setHours(23,59,59,999);
        return returnDate >= start && returnDate <= end;
      });
    }

    setFilteredReturns(filtered);
    // Keep selected IDs only if they are still in the filtered list
    setSelectedIds((prev) => prev.filter((id) => filtered.some((r) => r.id === id)));
  }, [searchQuery, startDate, endDate, returns]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredReturns.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleView = (returnData) => {
    setViewReturn(returnData);
  };

  const handleEdit = (id) => {
    // Navigate to the specific edit page for wastage
    navigate(`/edit-return/wastage/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteIds({ id: id, type: 'Wastage' }); // Ensure type is passed
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setError("Please select at least one wastage record to delete.");
      return;
    }
    setDeleteIds(
      selectedIds.map((id) => ({ id: id, type: 'Wastage' })) // Map selected IDs to {id, type} objects
    );
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async (ids) => {
  // console.log("Confirm Delete - IDs:", ids, "Type:", type);
  setLoading(true);
  setError("");
  try {
    const idsToDelete = Array.isArray(ids) ? ids : [ids];
    for (const id of idsToDelete) {
      const response = await fetch(`${API_BASE_URL}/api/return/${type}/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Failed to delete ${type} ID ${id}`);
      }
    }

    // Filter out deleted items from current state
    setReturns((prevReturns) =>
      prevReturns.filter((r) => !idsToDelete.includes(r.id))
    );
    setFilteredReturns((prevFiltered) =>
      prevFiltered.filter((r) => !idsToDelete.includes(r.id))
    );
    setSelectedIds([]); // Clear selections

    toast.success(
      idsToDelete.length > 1
        ? `${idsToDelete.length} wastage records deleted successfully.`
        : "Wastage record deleted successfully.",
      {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      }
    );
  } catch (err) {
    setError(err.message || "An error occurred while deleting the wastage records.");
    console.error("Delete error:", err);
    toast.error(`Failed to delete wastage record: ${err.message || "Network error"}`, {
      autoClose: 3000,
    });
  } finally {
    setLoading(false);
    setIsDeleteModalOpen(false); // Close modal
  }
};

  const formatBillNumber = (returnData) => {
    return returnData.billNumber || "N/A";
  };

  const calculateTotal = () => {
    return filteredReturns.reduce((sum, returnData) => sum + parseFloat(returnData.totalAmount || 0), 0).toFixed(2);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div>
      <PageHeading title="Wastage Returns" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Wastage Return List (Customer & Supplier)
            </h2>
            {selectedIds.length > 0 && (
              <button
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete Selected"
                onClick={handleBulkDelete}
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div>
              <input
                type="text"
                className="input-field"
                placeholder="Search By Bill or Name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Link to="/add-return" className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0">
              + Add Return
            </Link>
            <ExportOptions
              data={filteredReturns}
              title="Wastage Return List"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={toggleDropdown}
            />
          </div>
        </div>

        <div className="pt-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 px-4">
            <div className="flex items-center gap-2">
              <label htmlFor="startDate" className="label mb-0 shrink-0">
                Start Date:
              </label>
              <input
                type="date"
                className="input-field"
                value={startDate}
                id="startDate"
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="endDate" className="label mb-0 shrink-0">
                End Date:
              </label>
              <input
                type="date"
                className="input-field"
                value={endDate}
                id="endDate"
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="text-red-500 px-4 py-2">{error}</p>}

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[800px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="px-3 bg-[#FBFBFD]">
                  <th className="text-left">
                    <div className="relative left-3 -top-[1px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer"
                        checked={selectedIds.length === filteredReturns.length && filteredReturns.length > 0}
                        onChange={handleSelectAll}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Sl
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Bill Number
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Date
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Type
                  </th>
                  <th className="text-end uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Total Amount(৳)
                  </th>
                  <th className="text-end uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton rowCount={limit} columns={skeletonColumns} />
                ) : filteredReturns.length > 0 ? (
                  filteredReturns.map((returnData, index) => (
                    <tr key={`${returnData.sourceType}-${returnData.id}`} className="hover:bg-gray-50 border-b">
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`item-${returnData.id}`}
                            className="hidden peer"
                            checked={selectedIds.includes(returnData.id)}
                            onChange={() => handleSelect(returnData.id)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`item-${returnData.id}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {(currentPage - 1) * limit + index + 1}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {formatBillNumber(returnData)}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {returnData.medicineName}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {new Date(returnData?.wastedAt).toLocaleDateString()}
                      </td>
                      <td className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {returnData.sourceType}
                      </td>
                      <td className="text-end capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {returnData.totalAmount}
                      </td>
                      <td className="p-3 text-end space-x-2 print-hidden-in-print flex justify-end shrink-0">
                        <button
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          title="View"
                          onClick={() => handleView(returnData)}
                        >
                          <FaEye />
                        </button>
                        <button
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                          onClick={() => handleDelete(returnData.id)}
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center p-4 text-gray-500"> {/* Adjusted colspan */}
                      No wastage returns found
                    </td>
                  </tr>
                )}
                {!loading && filteredReturns.length > 0 && (
                  <tr>
                    <td
                      className="text-end capitalize font-inter font-bold text-md text-M-heading-color py-5 px-5 border-r"
                      colSpan={6}
                    >
                      Total:{" "}
                    </td>
                    <td className="text-end capitalize font-inter font-bold text-md text-M-heading-color py-5 px-3">
                      {calculateTotal()}
                    </td>
                    <td></td> {/* Empty cell */}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={returns.length}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
            />
          </div>

          <ViewReturnModal
            isOpen={!!viewReturn}
            onClose={() => setViewReturn(null)}
            returnData={viewReturn}
            onEdit={handleEdit}
            onDelete={confirmDelete}
            type="Wastage"
            formatBillNumber={formatBillNumber}
          />

          <ConfirmActionModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            ids={deleteIds}
            entityName="wastage record"
            title="Confirm Deletion"
            confirmText="Delete"
            cancelText="Cancel"
          />

          <ToastContainer />
        </div>
      </div>
    </div>
  );
};

export default WastageReturnList;